import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { Link } from 'react-router-dom';
import { NAV_LABELS } from '@/lib/nav-labels.js';
import { Radar10 } from '@/components/conselheiro/Radar10.js';
import { PlanoDoCiclo } from '@/components/conselheiro/PlanoDoCiclo.js';
import { CicloMensal } from '@/components/conselheiro/CicloMensal.js';
import { Concorrentes } from '@/components/conselheiro/Concorrentes.js';
import { DeclaracaoDeVinculos } from '@/components/conselheiro/DeclaracaoDeVinculos.js';
import { Indicacoes, type OrigemDaIndicacao } from '@/components/conselheiro/Indicacoes.js';
import { BalancoDoCiclo } from '@/components/conselheiro/BalancoDoCiclo.js';

/**
 * O Conselheiro no app. O conselheiro vê as empresas que atende; sócios e
 * responsáveis veem quem é o conselheiro da empresa e em que mês do ciclo
 * estão. O cadastro é feito no HAOps; preenchimento mensal, pauta, decisões e
 * a manhã chegam nas próximas etapas (docs/CONSELHEIRO_ANALISE.md §7).
 */

type Ciclo = { id: string; inicio_em: string; fim_em: string | null; mes: number } | null;
type Empresa = {
  id: string;
  nome: string;
  segmento: string | null;
  cidade: string | null;
  uf: string | null;
  faturamento_faixa: string | null;
};

type Painel = {
  conselheiro: {
    id: string;
    status: string;
    perfil: string;
    nda_aceito_em: string | null;
    certificado_em: string | null;
    certificacao_vence_em: string | null;
    empresas: {
      empresa: Empresa;
      meu_papel: string;
      desde: string;
      ciclo: Ciclo;
      pessoas: { nome: string; email: string; cargo: string | null; socio: boolean }[];
      outros_conselheiros: { nome: string; papel: string }[];
    }[];
  } | null;
  empresas: {
    empresa: Empresa;
    meu_papel: 'SOCIO' | 'RESPONSAVEL';
    ciclo: Ciclo;
    conselheiros: { nome: string; email: string; papel: string; cidade: string | null; uf: string | null; desde: string }[];
    socios: { nome: string; cargo: string | null }[];
    radares: {
      id: string;
      tipo: 'PARTIDA' | 'MARCO' | 'FINAL';
      data: string;
      aberto: boolean;
      notas: number[] | null;
      minha_resposta: { concluida: boolean; respondidas: number } | null;
    }[];
  }[];
};

const NOME_RADAR: Record<string, string> = { PARTIDA: 'Radar de partida', MARCO: 'Radar de marco', FINAL: 'Radar final' };

const PAPEL: Record<string, string> = { PRINCIPAL: 'Principal', CONVIDADO: 'Convidado', SENIOR: 'Sênior' };
const STATUS: Record<string, string> = { EM_FORMACAO: 'Em formação', CERTIFICADO: 'Certificado' };
const FAIXA: Record<string, string> = {
  ATE_1M: 'até R$ 1 mi',
  '1M_5M': 'R$ 1–5 mi',
  '5M_15M': 'R$ 5–15 mi',
  ACIMA_15M: 'acima de R$ 15 mi',
};

const data = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—';
const lugar = (cidade: string | null, uf: string | null) => [cidade, uf].filter(Boolean).join(' / ');

function Cartao({ titulo, sub, children, acao }: { titulo: string; sub?: ReactNode; children: ReactNode; acao?: ReactNode }) {
  return (
    <section className="rounded-card border border-ha-border2 bg-ha-card p-5">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-ha-text">{titulo}</h2>
          {sub ? <p className="mt-0.5 text-xs text-ha-muted">{sub}</p> : null}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

function Rotulo({ children }: { children: ReactNode }) {
  return <h3 className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-ha-muted">{children}</h3>;
}

function LinhaDoCiclo({ ciclo }: { ciclo: Ciclo }) {
  if (!ciclo) return <p className="text-sm text-ha-subtle">O ciclo de 12 meses ainda não começou.</p>;
  if (ciclo.mes === 0) return <p className="text-sm text-ha-subtle">O ciclo começa em {data(ciclo.inicio_em)}.</p>;
  return (
    <div>
      <p className="text-sm text-ha-text">
        Mês <strong>{ciclo.mes}</strong> de 12 <span className="text-ha-muted">· até {data(ciclo.fim_em)}</span>
      </p>
      <div className="mt-1.5 flex gap-0.5" aria-hidden>
        {Array.from({ length: 12 }, (_, i) => (
          <span key={i} className="h-1.5 flex-1 rounded-sm" style={{ background: i < ciclo.mes ? 'var(--primary)' : 'var(--border)' }} />
        ))}
      </div>
    </div>
  );
}

/**
 * O radar para o sócio: o autodiagnóstico a responder enquanto o radar está
 * aberto, e o radar que o conselheiro fechou, com partida × marco × final.
 */
function RadarDoSocio({ radares }: { radares: Painel['empresas'][number]['radares'] }) {
  const aberto = radares.find((r) => r.aberto);
  const fechados = radares.filter((r) => !r.aberto && r.notas);
  if (!aberto && fechados.length === 0) return null;
  const cores: Record<string, string> = { PARTIDA: '#94a3b8', MARCO: '#60a5fa', FINAL: 'var(--primary)' };
  return (
    <div className="md:col-span-2">
      <Rotulo>Radar</Rotulo>
      {aberto && aberto.minha_resposta ? (
        aberto.minha_resposta.concluida ? (
          <p className="mb-3 text-sm text-ha-subtle">
            Você concluiu o autodiagnóstico do {NOME_RADAR[aberto.tipo]?.toLowerCase()}. O conselheiro fecha o radar com as
            notas finais.
          </p>
        ) : (
          <div className="mb-3 flex flex-wrap items-center gap-3 rounded-card border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm text-amber-900">
              O conselheiro abriu o {NOME_RADAR[aberto.tipo]?.toLowerCase()}: 50 perguntas sobre a empresa, que você responde
              sozinho.
              {aberto.minha_resposta.respondidas > 0 ? ` Você já respondeu ${aberto.minha_resposta.respondidas}.` : ''}
            </p>
            <Link
              to={`/direcao/conselheiro/radar/${aberto.id}`}
              className="rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black"
              style={{ background: 'var(--primary)' }}
            >
              {aberto.minha_resposta.respondidas > 0 ? 'Continuar' : 'Responder'}
            </Link>
          </div>
        )
      ) : aberto ? (
        <p className="mb-3 text-sm text-ha-subtle">Os sócios estão respondendo o {NOME_RADAR[aberto.tipo]?.toLowerCase()}.</p>
      ) : null}
      {fechados.length > 0 ? (
        <Radar10
          tamanho={360}
          series={fechados.map((r) => ({
            rotulo: `${NOME_RADAR[r.tipo]} · ${data(r.data)}`,
            notas: r.notas!,
            cor: cores[r.tipo] ?? '#60a5fa',
            tracejada: r.tipo === 'PARTIDA' && fechados.length > 1,
            preenchida: r === fechados[fechados.length - 1],
          }))}
        />
      ) : null}
    </div>
  );
}

export function ConselheiroPage() {
  const painel = useQuery({
    queryKey: ['conselheiro-painel'],
    queryFn: async () => (await api.get<Painel>('/conselheiro/painel')).data,
  });

  const p = painel.data;
  const nada = p && !p.conselheiro && p.empresas.length === 0;
  // Quem pode indicar: o sócio, pela empresa dele; o conselheiro, por si.
  const origens: OrigemDaIndicacao[] = [
    ...(p?.empresas ?? [])
      .filter((e) => e.meu_papel === 'SOCIO')
      .map((e) => ({ empresa_id: e.empresa.id, rotulo: `Sócio da ${e.empresa.nome}` })),
    ...(p?.conselheiro ? [{ empresa_id: null, rotulo: 'Conselheiro' }] : []),
  ];

  return (
    <PageLayout
      title={NAV_LABELS.conselheiro}
      description="O conselho consultivo da sua empresa, mês a mês."
      actions={
        <Link
          to={p?.conselheiro ? '/ajuda/conselheiro-para-conselheiros' : '/ajuda/conselheiro-para-socios'}
          className="text-xs text-accent underline"
        >
          Como funciona
        </Link>
      }
    >
      {painel.isLoading ? <p className="text-sm text-ha-subtle">Carregando…</p> : null}
      {painel.isError ? <p className="text-sm text-red-600">Não foi possível carregar agora. Tente de novo em instantes.</p> : null}

      {nada ? (
        <div className="mx-auto max-w-lg rounded-card border border-ha-border2 bg-ha-card p-8 text-center">
          <h2 className="text-lg font-bold">Você ainda não está no Conselheiro</h2>
          <p className="mt-2 text-sm text-ha-subtle">
            Quando sua empresa entrar no programa, ou quando você for vinculado como conselheiro, ela aparece aqui.
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-6">
        {p?.empresas.map((e) => (
          <Cartao
            key={e.empresa.id}
            titulo={e.empresa.nome}
            sub={[e.meu_papel === 'SOCIO' ? 'Você é sócio' : 'Você é responsável', e.empresa.segmento, lugar(e.empresa.cidade, e.empresa.uf)]
              .filter(Boolean)
              .join(' · ')}
          >
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <Rotulo>Ciclo</Rotulo>
                <LinhaDoCiclo ciclo={e.ciclo} />
              </div>
              <div>
                <Rotulo>{e.conselheiros.length > 1 ? 'Conselheiros' : 'Conselheiro'}</Rotulo>
                {e.conselheiros.length === 0 ? (
                  <p className="text-sm text-ha-subtle">Ainda sem conselheiro vinculado.</p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {e.conselheiros.map((c) => (
                      <li key={c.email} className="text-sm">
                        <span className="font-medium text-ha-text">{c.nome}</span>{' '}
                        <span className="text-xs text-ha-muted">{PAPEL[c.papel] ?? c.papel}</span>
                        <p className="text-xs text-ha-muted">
                          <a href={`mailto:${c.email}`} className="text-accent underline">
                            {c.email}
                          </a>
                          {lugar(c.cidade, c.uf) ? ` · ${lugar(c.cidade, c.uf)}` : ''}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <RadarDoSocio radares={e.radares} />
              {e.meu_papel === 'SOCIO' ? <BalancoDoCiclo empresaId={e.empresa.id} empresa={e.empresa.nome} embutido /> : null}
              {e.ciclo ? (
                <div className="md:col-span-2">
                  <Rotulo>O mês</Rotulo>
                  <CicloMensal empresaId={e.empresa.id} principal={false} />
                </div>
              ) : null}
              {e.ciclo ? (
                <div className="md:col-span-2">
                  <Rotulo>Plano do ciclo</Rotulo>
                  <PlanoDoCiclo empresaId={e.empresa.id} podeDefinir={false} />
                </div>
              ) : null}
              {e.meu_papel === 'SOCIO' ? (
                <div className="md:col-span-2">
                  <Rotulo>Concorrentes</Rotulo>
                  <Concorrentes empresaId={e.empresa.id} />
                </div>
              ) : null}
              {e.socios.length > 0 ? (
                <div className="md:col-span-2">
                  <Rotulo>Sócios no conselho</Rotulo>
                  <p className="text-sm text-ha-text">
                    {e.socios.map((s) => (s.cargo ? `${s.nome} (${s.cargo})` : s.nome)).join(', ')}
                  </p>
                </div>
              ) : null}
            </div>
          </Cartao>
        ))}

        {p?.conselheiro ? (
          <>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-lg font-semibold text-ha-text">Empresas que você aconselha</h2>
              <p className="text-xs text-ha-muted">
                {STATUS[p.conselheiro.status] ?? p.conselheiro.status}
                {p.conselheiro.certificacao_vence_em
                  ? ` · certificação válida até ${data(p.conselheiro.certificacao_vence_em)}`
                  : ''}
              </p>
            </div>
            {!p.conselheiro.nda_aceito_em ? (
              <p className="rounded-card border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Seu NDA ainda não está registrado. Sem ele, nenhuma empresa pode ser vinculada a você — fale com a gestão
                do programa.
              </p>
            ) : null}
            {p.conselheiro.empresas.length === 0 ? (
              <p className="text-sm text-ha-subtle">Nenhuma empresa vinculada a você no momento.</p>
            ) : null}
            <Cartao titulo="Seus vínculos declarados" sub="Para que nenhuma empresa concorrente de onde você atua seja vinculada a você.">
              <DeclaracaoDeVinculos />
            </Cartao>
            {p.conselheiro.empresas.map((e) => (
              <Cartao
                key={e.empresa.id}
                titulo={e.empresa.nome}
                acao={
                  <Link
                    to={`/direcao/conselheiro/empresa/${e.empresa.id}`}
                    className="rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black"
                    style={{ background: 'var(--primary)' }}
                  >
                    Radar
                  </Link>
                }
                sub={[
                  `${PAPEL[e.meu_papel] ?? e.meu_papel} desde ${data(e.desde)}`,
                  e.empresa.segmento,
                  lugar(e.empresa.cidade, e.empresa.uf),
                  e.empresa.faturamento_faixa ? FAIXA[e.empresa.faturamento_faixa] : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              >
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <Rotulo>Ciclo</Rotulo>
                    <LinhaDoCiclo ciclo={e.ciclo} />
                    {e.outros_conselheiros.length > 0 ? (
                      <p className="mt-3 text-xs text-ha-muted">
                        Também nesta empresa:{' '}
                        {e.outros_conselheiros.map((o) => `${o.nome} (${PAPEL[o.papel] ?? o.papel})`).join(', ')}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Rotulo>Pessoas da empresa</Rotulo>
                    {e.pessoas.length === 0 ? (
                      <p className="text-sm text-ha-subtle">Sócios ainda não cadastrados.</p>
                    ) : (
                      <ul className="flex flex-col gap-1.5">
                        {e.pessoas.map((s) => (
                          <li key={s.email} className="text-sm">
                            <span className="font-medium text-ha-text">{s.nome}</span>{' '}
                            <span className="text-xs text-ha-muted">
                              {s.socio ? 'sócio' : 'responsável'}
                              {s.cargo ? ` · ${s.cargo}` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </Cartao>
            ))}
          </>
        ) : null}

        {origens.length > 0 ? (
          <Cartao titulo="Indique uma empresa" sub="A indicação chega à equipe comercial da Hack, e você acompanha aqui.">
            <Indicacoes origens={origens} />
          </Cartao>
        ) : null}
      </div>
    </PageLayout>
  );
}
